import { useEffect, useMemo, useRef, useState } from 'react'
import { emailCheck } from '../api'
import { isGroup, newId } from '../filterSpec'
import { n, pct } from '../format'

const POLL_MS = 700

/**
 * The email domain check: pick the column that holds the address, run the
 * check, read the verdict in plain words, and — if anything is broken — add a
 * filter that keeps the bad rows out of the table and out of every export.
 *
 * The exclusion is deliberately an ordinary filter rule rather than a switch in
 * here: it shows up in the trail and the Filters tool with its own ✕, it saves
 * with a view, and exports already follow the filters, so there is exactly one
 * mechanism deciding what a download holds.
 */

/** Best guess at the email column, same spirit as the duplicate-key suggestion. */
export function suggestEmailColumn(columns) {
  const named = columns.find((c) => /e?-?mail/i.test(c.name))
  if (named) return named.name
  return columns.find((c) => c.kind === 'text')?.name ?? columns[0]?.name ?? null
}

const EMAIL_OPS = new Set(['email_valid', 'email_invalid'])

/** The email rule the applied filters carry for this column, if any. */
function activeRule(applied, column) {
  for (const node of applied?.rules ?? []) {
    if (node.emailFor === column) {
      if (!isGroup(node)) return node.op
      return node.rules.some((r) => r.op === 'is_empty') ? 'valid_or_blank' : (node.rules[0]?.op ?? null)
    }
    if (!isGroup(node) && EMAIL_OPS.has(node.op) && node.column === column) return node.op
  }
  return null
}

const ACTIVE_TEXT = {
  email_valid: 'Filtering to rows with a valid email.',
  valid_or_blank: 'Filtering to rows with a valid email or none at all.',
  email_invalid: 'Showing only the rows with a broken email.',
}

export function EmailCheckPanel({
  datasetId,
  columns,
  column,
  setColumn,
  started,
  setStarted,
  applied,
  onAddRules,
}) {
  const [status, setStatus] = useState(null)
  const [error, setError] = useState(null)
  const [keepBlanks, setKeepBlanks] = useState(true)
  const [runId, setRunId] = useState(0)
  // Consumed by the first poll after "Check again": that one call tells the
  // server to throw the finished check away and run it afresh.
  const refreshRef = useRef(false)

  const textColumns = useMemo(() => columns.filter((c) => c.kind === 'text'), [columns])
  const options = textColumns.length > 0 ? textColumns : columns

  useEffect(() => {
    if (!started || !datasetId || !column) return
    let cancelled = false
    let timer = null
    const tick = async () => {
      const refresh = refreshRef.current
      refreshRef.current = false
      try {
        const s = await emailCheck({ id: datasetId, column, refresh })
        if (cancelled) return
        setStatus(s)
        setError(null)
        if (!s.done && !s.error) timer = setTimeout(tick, POLL_MS)
      } catch (e) {
        if (!cancelled) setError(e.message)
      }
    }
    tick()
    return () => {
      cancelled = true
      if (timer) clearTimeout(timer)
    }
  }, [started, datasetId, column, runId])

  const pick = (name) => {
    setColumn(name)
    setStarted(false)
    setStatus(null)
    setError(null)
  }

  const refresh = () => {
    refreshRef.current = true
    setStatus(null)
    setRunId((v) => v + 1)
  }

  const rule = (op) => ({
    id: newId(),
    emailFor: column,
    column,
    op,
    value: '',
    value2: '',
    values: [],
    caseSensitive: false,
  })

  const exclude = () => {
    if (!keepBlanks) return onAddRules(column, [rule('email_valid')])
    // "valid or blank" is an OR group, the same one the Filters tool would build.
    const blank = { ...rule('is_empty'), emailFor: undefined }
    onAddRules(column, [{ id: newId(), emailFor: column, combinator: 'OR', rules: [rule('email_valid'), blank] }])
  }

  const running = started && !error && (!status || (!status.done && !status.error))
  const done = !!status?.done
  const active = activeRule(applied, column)

  return (
    <section className="panel">
      <header className="panel-head">
        <h2>Emails</h2>
        {done && <span className="count">{n(status.domains)} domains</span>}
      </header>
      <p className="hint">
        Checks every email in a column: is it written correctly, and does its domain actually exist and
        accept mail? Domains are looked up through a public DNS resolver (Cloudflare) — only the domain
        names are sent, never your rows or addresses.
      </p>

      <select className="input" value={column ?? ''} onChange={(e) => pick(e.target.value)}>
        {options.map((c) => (
          <option key={c.name} value={c.name}>
            {c.name}
          </option>
        ))}
      </select>

      <div className="panel-actions tight">
        <button className="btn primary" onClick={() => setStarted(true)} disabled={!column || running || done}>
          {running ? 'Checking…' : 'Check email domains'}
        </button>
        {done && (
          <button className="btn tiny" onClick={refresh} title="Run the check on this column again">
            Check again
          </button>
        )}
      </div>

      {running && status && (
        <p className="hint">
          {status.phase === 'scan'
            ? `reading the file · ${pct(status.progress)}`
            : `asking about domains · ${n(status.checked)} of ${n(status.domains)}`}
        </p>
      )}
      {(error || status?.error) && <p className="footnote warn-text">{error ?? status.error}</p>}

      {done && (
        <>
          <hr className="panel-rule" />
          <p className="hint">
            <strong>{n(status.withEmail)}</strong> row{status.withEmail === 1 ? '' : 's'} with an email ·{' '}
            <strong>{n(status.validRows)}</strong> valid · <strong>{n(status.invalidRows)}</strong> broken
            {status.empty > 0 && <> · {n(status.empty)} blank</>}
          </p>
          {status.badSyntax > 0 && (
            <p className="footnote">
              {n(status.badSyntax)} row{status.badSyntax === 1 ? ' does' : 's do'} not read as an email
              address
              {status.badSyntaxSamples.length > 0 && <> — e.g. “{status.badSyntaxSamples.join('”, “')}”</>}
            </p>
          )}
          {status.unknownRows > 0 && (
            <p className="footnote">
              {n(status.unknownRows)} row{status.unknownRows === 1 ? ' uses' : 's use'}{' '}
              {n(status.unknownDomains)} domain{status.unknownDomains === 1 ? '' : 's'} that could not be
              checked — those count as valid.
            </p>
          )}
          {status.fromCache > 0 && (
            <p className="footnote">
              {n(status.fromCache)} of {n(status.domains)} domain{status.domains === 1 ? ' was' : 's were'}{' '}
              already known from an earlier check.
            </p>
          )}

          {status.badDomains > 0 && (
            <div className="value-options">
              {status.badList.map((b) => (
                <div key={b.domain} className="value-option" title={b.reason}>
                  <span className="value-text">{b.domain}</span>
                  <span className="value-count">{n(b.rows)}</span>
                </div>
              ))}
              {status.badListTruncated && (
                <span className="muted">…and {n(status.badDomains - status.badList.length)} more</span>
              )}
            </div>
          )}

          <hr className="panel-rule" />
          {active ? (
            <>
              <p className="hint">{ACTIVE_TEXT[active] ?? 'An email filter is on this column.'}</p>
              <div className="panel-actions tight">
                <button className="btn subtle" onClick={() => onAddRules(column, [])}>
                  Remove the email filter
                </button>
              </div>
            </>
          ) : status.invalidRows > 0 ? (
            <>
              <label className="value-option">
                <input type="checkbox" checked={keepBlanks} onChange={(e) => setKeepBlanks(e.target.checked)} />
                <span className="value-text">Keep rows with no email too</span>
              </label>
              <div className="panel-actions tight">
                <button className="btn primary" onClick={exclude}>
                  Exclude broken emails
                </button>
                <button
                  className="btn"
                  onClick={() => onAddRules(column, [rule('email_invalid')])}
                  title="See exactly which rows would go, before excluding them"
                >
                  Show the broken rows
                </button>
              </div>
              <p className="footnote">
                This adds a filter — the table, the counts, and every export follow it. Remove it any time
                from the trail above or the Filters tool.
              </p>
            </>
          ) : (
            <p className="hint">Every email that could be checked is valid — nothing to exclude.</p>
          )}
        </>
      )}
    </section>
  )
}
